import { ActivatedRoute, Params, Router } from '@angular/router';
import { BlogArticle, BlogArticleAdapter } from '@shared/models/blog-article.model';
import { BlogAuthor } from '@shared/models/blog-author.model';
import { BlogPage, BlogPageAdapter } from '@shared/models/blog-page.model';
import { BlogService, SocialShareService, TrackingService } from '@core/services';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { environment } from '@env/environment';
import { take } from 'rxjs/operators';
import { ISocialShare } from '@core/seo/social-share/social-share.interface';
import { JsonLdService } from '@core/seo/json-ld/json-ld.service';
import { Location } from '@angular/common';

@Component({
    selector: 'gh-author',
    templateUrl: 'author.component.html',
    standalone: false
})

export class AuthorComponent implements OnInit, OnDestroy {
  author: BlogAuthor;
  altAuthor: BlogAuthor;
  articlesByAuthor: BlogArticle[];
  pager: BlogPage;
  altPager: BlogPage;
  currentPage: number = 1;
  pageIndex: number = 0;
  totalPages: number;
  altTotalPages: number;

  private parametersObservable: any;

  constructor(
    private blogArticleAdapter: BlogArticleAdapter,
    private blogPageAdapter: BlogPageAdapter,
    private blogService: BlogService,
    private jsonLdService: JsonLdService,
    private socialShareService: SocialShareService,
    private trackingService: TrackingService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected router: Router
  ) { }

  ngOnInit() {
    this.parametersObservable = this.route.params
      .subscribe((params) => {
        // "author" is obtained from 'AuthorResolver'
        this.author = this.route.snapshot.data['author'];
        if (!this.author || !this.author.slug) {
          this.notFound();
          return;
        }

        this.currentPage = +params.page ? +params.page : 1;
        this.pageIndex = this.currentPage - 1;
        this.getArticlesForAuthor(this.author.id, this.pageIndex.toString());
        this.getAltAuthorId();
      });
  }

  gotoPage(ev: any) {
    this.getArticlesForAuthor(this.author.id, ev.pageIndex);
  }

  ngOnDestroy() {
    this.parametersObservable?.unsubscribe();
  }

  private getArticlesForAuthor(authorId: number, pageNumber: string) {
    this.blogService.getArticlesForAuthor(authorId, pageNumber)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res || res.error) {
          return;
        }

        if (pageNumber > res.totalPages) {
          this.router.navigate([`/author/${this.author.slug}/page/${res.totalPages}`]);
        }

        this.pager = this.blogPageAdapter.adapt(res);
        const articles: BlogArticle[] = res.content || [];
        this.articlesByAuthor = [];
        this.totalPages = this.pager.totalPages;

        if (this.currentPage > this.totalPages) {
          this.notFound();
          return;
        }

        articles.forEach((article) => {
          this.articlesByAuthor.push(this.blogArticleAdapter.adapt(article));
        });
      });
  }

  private getAltAuthorId() {
    const locale = environment.locale === 'en-us' ? 'en-gb' : 'en-us';

    this.blogService.getAuthorBySlug(this.author.slug, locale)
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.altAuthor = res;
          this.getAltTotalPages(locale);
        } else {
          this.setSeoData(this.author);
        }
      });
  }

  private getAltTotalPages(locale: string) {
    this.blogService.getArticlesForAuthor(this.altAuthor.id, '0', locale)
      .pipe(take(1))
      .subscribe((res) => {
        if (res && !res.error) {
          this.altPager = this.blogPageAdapter.adapt(res);
          this.altTotalPages = this.altPager.totalPages;
        }
        this.setSeoData(this.author);
      });
  }

  private notFound() {
    this.router.navigate(['/404']);
  }

  private setSeoData(author: BlogAuthor) {
    const seoData: ISocialShare = {
      title: `${author.fullName} author | GoHenry`,
      keywords: `gohenry, blog, ${author.fullName}, author`,
      description: author.description,
      image: author.authorImageUrl,
      url: environment.location + this.location.path(),
      type: 'article.author',
      author: 'gohenry',
      section: `author.${author.slug}`,
      published: '',
      modified: '',
      totalPages: [this.currentPage, this.totalPages, this.altTotalPages]
    };

    const jsonLdObject = this.jsonLdService.getObject('Website', {
      name: 'GoHenry | Making every kid good with money – blog',
      url: seoData.url,
      description: 'The GoHenry pocket money card is a smart solution for managing your child\'s money and giving money to children, through an allowance prepaid card.'
    });

    const jsonLdObject2 = this.jsonLdService.getObject('Organization', {
      url: environment.website,
      logo: environment.jsonImage,
      legalName: 'GoHenry',
      name: 'GoHenry'
    });

    this.socialShareService.setData(seoData);
    this.jsonLdService.setData([jsonLdObject, jsonLdObject2]);
    this.trackingService.setDataLayerAttributes({
      event: 'pageView',
      event_name: 'page_view',
      page: {
        location: this.location.path(),
        environment: environment.env,
        locale: environment.locale,
        title: seoData.title,
        feature: 'blog',
      }
    });
  }

}
